import React, { useEffect, useMemo } from 'react';
import { toastMessage } from '@croquiscom-pvt/content-ui';
import styled from '@emotion/styled';
import flatten from 'lodash/flatten';

import { BundlePurchaseItemList } from '@common/checkout-utils';

import { ItemCard } from '../../components/ItemCard/ItemCard';
import { Layout } from '../../components/Layout';
import { TextField } from '../../components/TextField';
import { TitledHead, TitledHeadType } from '../../components/TitledHead';
import { MinimumOrderQuantityType } from '../../constant';
import { useMinimumOrderQuantityToastMessage } from '../../hooks/useMinimumOrderQuantityToastMessage';
import { useOptionsControl } from '../../hooks/useOptionsControl';
import { useOptions } from '../../queries/useOptions';
import { SelectedItem } from '../../types/option-type';
import { ConfirmContainer } from '../confirmation/ConfirmContainer';
import { ShippingDetailContainer } from '../notification/ShippingDetailContainer';

interface OnlyCustomOptionViewProps {
  isLoggedIn: boolean;
  onClose?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
}

export const OnlyCustomOptionView: React.FC<React.PropsWithChildren<OnlyCustomOptionViewProps>> = ({
  isLoggedIn,
  onClose,
  onAddCart,
}) => {
  const {
    options: {
      catalog_product: { minimum_order_quantity, minimum_order_quantity_type },
    },
  } = useOptions();

  const {
    isItemsShown,
    itemCards,
    isShowShippingDetail,
    textOptions,
    optionMatchedItem,
    setIsShowShippingDetail,
    enterOption,
    addItem,
    changeItemCounts,
    removeItem,
    resetAll,
  } = useOptionsControl();
  const isOrderQuantityItemType = minimum_order_quantity_type === MinimumOrderQuantityType.ITEM;
  const orderQuantityToastMessage = useMinimumOrderQuantityToastMessage(
    minimum_order_quantity_type,
    minimum_order_quantity,
  );
  const hasRequiredField = useMemo(() => flatten(textOptions).filter((o) => o.is_required).length > 0, [textOptions]);
  const addSelectedItem = () => {
    const [item] = optionMatchedItem;
    if (item) {
      const count = minimum_order_quantity && minimum_order_quantity > 0 ? minimum_order_quantity : 1;
      const selectedItem = {
        ...item,
        count,
        options: [],
        text_options: textOptions,
        origin_items: [item],
      } as unknown as SelectedItem;

      addItem(selectedItem);
      resetAll();
    }
  };

  const handleOptionBlur = (parent_key: number, is_required: boolean) => (value: string) => {
    try {
      const has_required_field = textOptions[parent_key]?.filter((o) => o.is_required).length > 0;

      if (value && (!has_required_field || is_required)) {
        addSelectedItem();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChangeItemCount = (item: SelectedItem, count: number) => {
    if (minimum_order_quantity && minimum_order_quantity > 0 && count < minimum_order_quantity) {
      toastMessage(orderQuantityToastMessage);
      changeItemCounts({ item, count: minimum_order_quantity });
      return;
    }
    changeItemCounts({ item, count });
  };

  const renderTextOptions = () => {
    return textOptions.map((current_text_option_list, option_key) => (
      <SelectOptionContainer key={`only-custom-option-view--${option_key}`}>
        {current_text_option_list.map(({ name, is_required, max_length, selected_value }) => {
          return (
            <TextField
              key={name}
              name={name}
              is_required={is_required}
              max_length={Number(max_length)}
              value={selected_value}
              onChange={(v) => enterOption({ name, value: v })}
              onBlur={handleOptionBlur(option_key, is_required)}
              onKeyPress={(code, target) => {
                if (code === 13) {
                  target.blur();
                }
              }}
            />
          );
        })}
      </SelectOptionContainer>
    ));
  };

  const renderItemCards = () =>
    itemCards.map(({ item, optionNames, minCount, maxCount }) => {
      return (
        <ItemCard
          key={optionNames}
          optionNames={optionNames}
          count={item.count}
          price={item.price}
          minCount={minimum_order_quantity && minimum_order_quantity > minCount ? minimum_order_quantity : minCount}
          maxCount={maxCount}
          isOrderQuantityItemType={isOrderQuantityItemType}
          onChangeItemCount={(v) => handleChangeItemCount(item, v)}
          onRemove={() => removeItem(item)}
        />
      );
    });

  useEffect(() => {
    if (!hasRequiredField && itemCards.length === 0) {
      addSelectedItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequiredField, itemCards]);

  if (isShowShippingDetail) {
    return (
      <Layout
        header={<TitledHead type={TitledHeadType.SHIPPING_DETAIL} onClickBack={() => setIsShowShippingDetail(false)} />}
      >
        <ShippingDetailContainer />
      </Layout>
    );
  }

  return (
    <Layout
      footer={isItemsShown && <ConfirmContainer onClose={onClose} onAddCart={onAddCart} isLoggedIn={isLoggedIn} />}
    >
      {renderTextOptions()}
      {renderItemCards()}
    </Layout>
  );
};

const SelectOptionContainer = styled.div`
  margin-bottom: 20px;
`;
